import { useEffect, useState } from "react";
import { Download, ExternalLink, LogOut, Package, Plus, RefreshCw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, gbp, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TriskelionMark from "@/components/TriskelionMark";
import { T } from "@/constants/testIds";

const EMPTY_FORM = {
  name: "",
  description: "",
  category: "T-Shirts",
  price: "",
  compare_at_price: "",
  sizes: "S, M, L, XL",
  delivery_time: "4-7 days UK & Isle of Man",
  temu_link: "",
  temu_price: "",
  tag: "",
  stock: 50,
  colours: "",
  featured: false,
  active: true,
  images: [],
};

const STATUS_STYLES = {
  pending_payment: "bg-stone-900/10 text-stone-700 border-stone-400",
  paid: "bg-[#F5C518]/20 text-[#C79500] border-[#F5C518]/50",
  ordered: "bg-blue-500/20 text-blue-700 border-blue-400/50",
  shipped: "bg-[#E52E39]/20 text-[#E52E39] border-[#E52E39]/50",
  delivered: "bg-green-500/20 text-green-700 border-green-400/50",
  refunded: "bg-violet-500/15 text-violet-700 border-violet-400/50",
  cancelled: "bg-red-100 text-red-700 border-red-700/60",
};

function LoginView() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm border border-stone-200 bg-[#FFFFFF] p-8">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/logo.png" alt="V.I.A logo" className="h-14 w-14 object-cover" />
          <h1 className="font-display text-3xl font-black uppercase tracking-wide text-stone-950">V.I.A HQ</h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">Owner access only</p>
        </div>
        <div className="space-y-4">
          <div>
            <p className="via-label">Email</p>
            <input
              data-testid={T.adminLoginEmail}
              type="email"
              className="via-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@via.im"
            />
          </div>
          <div>
            <p className="via-label">Password</p>
            <input
              data-testid={T.adminLoginPassword}
              type="password"
              className="via-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
            />
          </div>
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 border border-[#E52E39]/40 bg-[#E52E39]/10 px-3 py-2 text-xs text-[#E52E39]">
            {error}
          </p>
        )}
        <button
          data-testid={T.adminLoginSubmit}
          type="submit"
          disabled={busy}
          className="mt-6 w-full bg-[#E52E39] py-3.5 font-display text-lg font-black uppercase tracking-wide text-white transition-colors hover:bg-[#c9202a] disabled:opacity-60"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function ProductForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd);
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
        toast.success("Image uploaded");
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      }
    }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: form.name,
      description: form.description,
      category: form.category,
      price: parseFloat(form.price),
      compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
      sizes: form.sizes.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
      colours: form.colours.split(",").map((s) => s.trim()).filter(Boolean),
      delivery_time: form.delivery_time,
      temu_link: form.temu_link,
      temu_price: form.temu_price ? parseFloat(form.temu_price) : null,
      stock: parseInt(form.stock) || 0,
      tag: form.tag,
      featured: form.featured,
      active: form.active,
      images: form.images,
    };
    try {
      if (form.id) {
        await api.put(`/products/${form.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      if (!payload.images.length) {
        toast.warning("Saved — but add a photo (edit the product) so it shows in the shop");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save product");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="border border-[#F5C518]/40 bg-[#FFFFFF] p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-2xl font-black uppercase text-stone-950">{form.id ? "Edit product" : "New product"}</h3>
        <button type="button" onClick={onCancel} className="text-stone-500 hover:text-stone-950" aria-label="Close form">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <p className="via-label">Name</p>
          <input required className="via-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Manx TT Hoodie" />
        </div>
        <div className="sm:col-span-2">
          <p className="via-label">Description</p>
          <textarea className="via-input min-h-[80px]" value={form.description} onChange={(e) => set("description", e.target.value)} />
        </div>
        <div>
          <p className="via-label">Price (£)</p>
          <input required type="number" step="0.01" min="0" className="via-input" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="65" />
        </div>
        <div>
          <p className="via-label">Compare-at price (£, optional)</p>
          <input type="number" step="0.01" min="0" className="via-input" value={form.compare_at_price || ""} onChange={(e) => set("compare_at_price", e.target.value)} />
        </div>
        <div>
          <p className="via-label">Category</p>
          <select className="via-input" value={form.category} onChange={(e) => set("category", e.target.value)}>
            {["T-Shirts", "Hoodies", "Sweatshirts", "Outerwear", "Accessories"].map((c) => (
              <option key={c} className="bg-[#FFFFFF]">{c}</option>
            ))}
          </select>
        </div>
        <div>
          <p className="via-label">Sizes (comma separated)</p>
          <input className="via-input" value={form.sizes} onChange={(e) => set("sizes", e.target.value)} />
        </div>
        <div>
          <p className="via-label">Delivery time</p>
          <input required className="via-input" value={form.delivery_time} onChange={(e) => set("delivery_time", e.target.value)} placeholder="4-7 days UK & Isle of Man" />
        </div>
        <div>
          <p className="via-label">Tag (optional)</p>
          <input className="via-input" value={form.tag} onChange={(e) => set("tag", e.target.value)} placeholder="BESTSELLER" />
        </div>
        <div>
          <p className="via-label">Colours (comma separated — shown to customers)</p>
          <input className="via-input" value={form.colours} onChange={(e) => set("colours", e.target.value)} placeholder="Black, White, Red" />
        </div>
        <div>
          <p className="via-label">Stock</p>
          <input type="number" min="0" className="via-input" value={form.stock} onChange={(e) => set("stock", e.target.value)} />
        </div>
        <div className="sm:col-span-1">
          <p className="via-label">Temu link (never shown to customers)</p>
          <input className="via-input" value={form.temu_link} onChange={(e) => set("temu_link", e.target.value)} placeholder="https://www.temu.com/goods.html?..." />
        </div>
        <div className="sm:col-span-1">
          <p className="via-label">Temu price (£ — your cost, for margin records)</p>
          <input type="number" step="0.01" min="0" className="via-input" value={form.temu_price || ""} onChange={(e) => set("temu_price", e.target.value)} placeholder="8.43" />
        </div>
        <div className="sm:col-span-2">
          <p className="via-label">Photo</p>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 border-2 border-dashed border-stone-300 bg-[#FAF8F4] px-4 py-8 text-center transition-colors hover:border-[#E52E39]">
            <input
              data-testid="admin-photo-upload"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={(e) => uploadFiles(e.target.files)}
              className="hidden"
            />
            <span className="font-display text-xl font-bold uppercase tracking-wide text-stone-950">
              Upload a photo from your device
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-stone-400">
              Click here, then choose the picture saved on your laptop (jpg / png / webp)
            </span>
          </label>
          {uploading && <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#C79500]">Uploading…</p>}
          {!uploading && form.images.length === 0 && (
            <p className="mt-2 border border-[#C79500]/50 bg-[#FFFBEF] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-[#C79500]">
              No photo yet — items without a photo show a blank placeholder in the shop
            </p>
          )}
          {form.images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {form.images.map((im, i) => (
                <div key={i} className="relative h-24 w-20 overflow-hidden border border-stone-200">
                  <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => set("images", form.images.filter((_, j) => j !== i))}
                    className="absolute right-0 top-0 bg-[#E52E39] p-1 text-white"
                    aria-label="Remove image"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#E52E39]" />
          Feature on homepage
        </label>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#E52E39]" />
          Visible in shop
        </label>
      </div>
      <button
        data-testid={T.adminProductSave}
        type="submit"
        disabled={busy || uploading}
        className="mt-6 bg-[#E52E39] px-8 py-3 font-display text-lg font-black uppercase tracking-wide text-white hover:bg-[#c9202a] disabled:opacity-60"
      >
        {busy ? "Saving…" : "Save product"}
      </button>
    </form>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importPrice, setImportPrice] = useState("");
  const [importMargin, setImportMargin] = useState("35");
  const [customers, setCustomers] = useState([]);
  const [grantAmounts, setGrantAmounts] = useState({});
  const [importPhotos, setImportPhotos] = useState([]);
  const [importPhotosBusy, setImportPhotosBusy] = useState(false);
  const [pickedPhotos, setPickedPhotos] = useState([]);
  const [photoNote, setPhotoNote] = useState("");

  const refund = async (orderId, mode) => {
    if (!window.confirm(mode === "card" ? "Refund this order to the customer's card?" : "Refund this order as store credit?")) return;
    try {
      await api.post(`/admin/orders/${orderId}/refund`, { mode });
      toast.success(mode === "card" ? "Refunded to card" : "Refunded as store credit");
      loadOrders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Refund failed");
    }
  };

  const grantCredit = async (userId, amount) => {
    try {
      await api.post(`/admin/customers/${userId}/credit`, { amount: parseFloat(amount) });
      toast.success("Credit granted");
      loadCustomers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not grant credit");
    }
  };

  const loadOrders = () =>
    api.get("/orders").then((r) => setOrders(r.data || [])).catch(() => setOrders([]));
  const loadProducts = () =>
    api.get("/admin/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([]));

  const loadCustomers = () =>
    api.get("/admin/customers").then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));

  const computeRetail = (tp, margin) => {
    const raw = tp * (1 + (parseFloat(margin) || 35) / 100);
    return (Math.ceil(raw - 0.99) + 0.99).toFixed(2);
  };

  const fetchPhotos = async () => {
    if (!importUrl.trim()) {
      toast.error("Paste the Temu link first");
      return;
    }
    setImportPhotosBusy(true);
    setImportPhotos([]);
    setPickedPhotos([]);
    setPhotoNote("");
    try {
      const { data } = await api.post("/admin/fetch-images", { url: importUrl.trim() });
      setImportPhotos(data.images || []);
      setPhotoNote(
        data.fetched
          ? `Found ${data.images.length} photo(s) — tap any that show just the garment. If these are only Temu logos, save the real shots from the listing page to your laptop and upload them in the form instead.`
          : "Temu blocked automatic photo grabbing for this link — no problem: open the link, save the garment-only shots to your laptop, and upload them in the form instead."
      );
    } catch {
      setPhotoNote("Couldn't fetch photos from this link — upload the photo from your laptop in the form instead.");
    } finally {
      setImportPhotosBusy(false);
    }
  };

  const pickPhoto = async (url) => {
    if (pickedPhotos.includes(url)) {
      setPickedPhotos((p) => p.filter((u) => u !== url));
      return;
    }
    try {
      const { data } = await api.post("/admin/import-image", { url });
      setPickedPhotos((p) => [...p, data.url]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save that photo");
    }
  };

  const useImported = () => {
    const tp = parseFloat(importPrice);
    if (!importUrl.trim() || !tp || tp <= 0) {
      toast.error("Paste the Temu link and your cost price first");
      return;
    }
    setEditing({
      ...EMPTY_FORM,
      temu_link: importUrl.trim(),
      temu_price: String(tp),
      price: computeRetail(tp, importMargin),
      images: pickedPhotos,
    });
    setShowForm(true);
    setShowImport(false);
    setImportPhotos([]);
    setPickedPhotos([]);
    setPhotoNote("");
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    loadOrders();
    loadProducts();
    loadCustomers();
  }, []);

  const setStatus = async (orderId, status) => {
    try {
      await api.patch(`/orders/${orderId}`, { status });
      toast.success("Order updated");
      loadOrders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update order");
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      loadProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete");
    }
  };

  const startEdit = (p) => {
    setEditing({
      ...EMPTY_FORM,
      ...p,
      price: String(p.price),
      compare_at_price: p.compare_at_price ? String(p.compare_at_price) : "",
      sizes: (p.sizes || []).join(", "),
      colours: (p.colours || []).join(", "),
      stock: String(p.stock ?? 50),
    });
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="via-label">Signed in as {user?.email}</p>
            <h1 className="font-display text-4xl font-black uppercase tracking-tight text-stone-950 sm:text-5xl">V.I.A HQ</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                loadOrders();
                loadProducts();
              }}
              className="flex h-11 w-11 items-center justify-center border border-stone-300 text-stone-700 hover:border-[#F5C518] hover:text-[#C79500]"
              aria-label="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 border border-stone-300 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-700 hover:border-[#E52E39] hover:text-[#E52E39]"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-2 border-b border-stone-200 pb-4">
          {[
            ["orders", `Orders (${orders.length})`],
            ["products", `Products (${products.length})`],
            ["customers", `Customers (${customers.length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                tab === key ? "bg-[#E52E39] text-white" : "border border-stone-300 text-stone-700 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="mt-8 space-y-4">
            {orders.length === 0 && (
              <p className="py-16 text-center font-mono text-sm uppercase tracking-widest text-stone-400">
                No orders yet — they'll appear here the moment one lands.
              </p>
            )}
            {orders.map((o) => (
              <div key={o.id} data-testid="admin-order-card" className="border border-stone-200 bg-[#FFFFFF] p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 pb-3">
                  <div>
                    <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-stone-950">
                      #{o.id.slice(0, 8).toUpperCase()}
                    </p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-stone-400">
                      {new Date(o.created_at).toLocaleString("en-GB")} • {o.currency} {gbp(o.total)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[o.status] || ""}`}>
                      {o.status.replace("_", " ")}
                    </span>
                    <select
                      data-testid={T.adminOrderStatus(o.id)}
                      value={o.status}
                      onChange={(e) => setStatus(o.id, e.target.value)}
                      className="border border-stone-300 bg-[#FAF8F4] px-2 py-1.5 font-mono text-[11px] uppercase text-stone-800 outline-none focus:border-[#E52E39]"
                    >
                      {["pending_payment", "paid", "ordered", "shipped", "delivered", "cancelled"].map((s) => (
                        <option key={s} value={s} className="bg-[#FFFFFF]">
                          {s.replace("_", " ")}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {(o.status === "paid" || o.status === "ordered" || o.status === "shipped") && (
                  <div className="mb-3 mt-3 flex flex-wrap gap-2">
                    <button
                      data-testid={T.adminRefundCard(o.id)}
                      onClick={() => refund(o.id, "card")}
                      className="border border-stone-300 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-stone-600 hover:border-[#E52E39] hover:text-[#E52E39]"
                    >
                      Refund to card
                    </button>
                    <button
                      data-testid={T.adminRefundCredit(o.id)}
                      onClick={() => refund(o.id, "credit")}
                      className="border border-[#C79500]/60 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-[#C79500] hover:bg-[#F5C518]/10"
                    >
                      Refund as credit
                    </button>
                  </div>
                )}
                <div className="grid gap-4 md:grid-cols-[240px_1fr]">
                  <div className="text-xs leading-relaxed text-stone-700">
                    <p className="via-label">Customer</p>
                    <p className="font-semibold text-stone-950">{o.customer?.name}</p>
                    <p className="text-stone-500">{o.customer?.email}</p>
                    <p className="mt-2 text-stone-500">
                      {[o.customer?.address_line, o.customer?.city, o.customer?.postcode, o.customer?.country].filter(Boolean).join(", ")}
                    </p>
                  </div>
                  <div>
                    <p className="via-label">Items — buy the size on Temu</p>
                    <ul className="space-y-2">
                      {o.items.map((it, i) => (
                        <li key={i} className="flex flex-wrap items-center justify-between gap-2 border border-stone-200 bg-[#FAF8F4] px-3 py-2">
                          <span className="text-sm text-stone-800">
                            {it.name} <span className="font-mono text-[10px] uppercase tracking-widest text-[#C79500]">• size {it.size}{it.colour ? ` • ${it.colour}` : ""} × {it.qty}</span>
                          </span>
                          <span className="flex items-center gap-3">
                            <span className="font-mono text-xs text-stone-500">{gbp(it.unit_price * it.qty)}</span>
                            {it.temu_link ? (
                              <a
                                data-testid={T.adminOrderTemu(o.id)}
                                href={it.temu_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 bg-[#E52E39] px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-[#c9202a]"
                              >
                                Buy on Temu <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              <span className="font-mono text-[10px] uppercase tracking-widest text-stone-400">No link</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "customers" && (
          <div className="mt-8 space-y-4">
            {customers.length === 0 && (
              <p className="py-16 text-center font-mono text-sm uppercase tracking-widest text-stone-500">
                No customer accounts yet — they appear here as soon as someone registers.
              </p>
            )}
            {customers.map((c) => (
              <div key={c.id} data-testid="admin-customer-card" className="flex flex-wrap items-center justify-between gap-4 border border-stone-200 bg-white p-4">
                <div>
                  <p className="font-display text-lg font-bold uppercase leading-tight text-stone-950">{c.name || c.email}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-stone-400">
                    {c.email} • {c.orders_count || 0} orders
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span data-testid="admin-customer-credit" className="border border-[#C79500]/50 bg-[#F5C518]/10 px-3 py-1.5 font-mono text-xs font-bold text-[#C79500]">
                    Credit {gbp(c.credit_balance || 0)}
                  </span>
                  <input
                    data-testid={`admin-grant-credit-input-${c.id}`}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="£ amount"
                    value={grantAmounts[c.id] || ""}
                    onChange={(e) => setGrantAmounts((g) => ({ ...g, [c.id]: e.target.value }))}
                    className="via-input w-28"
                  />
                  <button
                    data-testid={T.adminGrantCredit(c.id)}
                    onClick={() => grantCredit(c.id, grantAmounts[c.id])}
                    className="bg-[#E52E39] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-[#c9202a]"
                  >
                    Grant credit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <div className="mt-8 space-y-4">
            <div className="flex flex-wrap justify-end gap-2">
              <button
                data-testid="admin-import-temu-button"
                onClick={() => setShowImport(!showImport)}
                className="inline-flex items-center gap-2 border border-[#C79500] px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#C79500] hover:bg-[#F5C518]/10"
              >
                <Download className="h-4 w-4" /> Import from Temu
              </button>
              <button
                data-testid={T.adminAddProduct}
                onClick={() => {
                  setEditing(null);
                  setShowForm(!showForm);
                }}
                className="inline-flex items-center gap-2 bg-[#E52E39] px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:bg-[#c9202a]"
              >
                <Plus className="h-4 w-4" /> Add product
              </button>
            </div>
            {showImport && (
              <div className="border border-[#C79500]/50 bg-[#FFFBEF] p-5">
                <p className="via-label">Import from Temu — paste the product link and your cost price; your margin is added automatically</p>
                <div className="grid gap-3 sm:grid-cols-[1fr_150px_120px]">
                  <input data-testid="import-temu-url" className="via-input" value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://www.temu.com/goods.html?goods_id=..." />
                  <input data-testid="import-temu-cost" type="number" step="0.01" min="0" className="via-input" value={importPrice} onChange={(e) => setImportPrice(e.target.value)} placeholder="Your cost 8.43" />
                  <input data-testid="import-temu-margin" type="number" min="0" className="via-input" value={importMargin} onChange={(e) => setImportMargin(e.target.value)} placeholder="Margin %" />
                </div>
                <div className="mt-3">
                  <button
                    data-testid="import-temu-photos"
                    onClick={fetchPhotos}
                    disabled={importPhotosBusy}
                    className="border border-stone-300 bg-white px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-stone-600 hover:border-[#E52E39] hover:text-[#E52E39] disabled:opacity-60"
                  >
                    {importPhotosBusy ? "Looking for photos…" : "Try to auto-fetch photos from the link"}
                  </button>
                  {photoNote && <p className="mt-2 text-xs leading-relaxed text-stone-500">{photoNote}</p>}
                  {importPhotos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {importPhotos.map((u) => {
                        const picked = pickedPhotos.includes(u);
                        return (
                          <button
                            key={u}
                            type="button"
                            onClick={() => pickPhoto(u)}
                            className={`relative h-24 w-20 overflow-hidden border-2 transition-all ${picked ? "border-[#E52E39]" : "border-transparent opacity-80 hover:opacity-100"}`}
                            aria-label={picked ? "Remove from selection" : "Add this photo"}
                          >
                            <img src={u} alt="" className="h-full w-full object-cover" />
                            {picked && <span className="absolute inset-0 bg-[#E52E39]/20" />}
                          </button>
                        );
                      })}
                      {pickedPhotos.length > 0 && (
                        <p className="w-full font-mono text-[10px] uppercase tracking-widest text-[#C79500]">
                          {pickedPhotos.length} photo{pickedPhotos.length > 1 ? "s" : ""} selected — they'll be added when you click "Use these details"
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500">
                    {(() => {
                      const tp = parseFloat(importPrice);
                      return tp > 0 ? (
                        <span>
                          Temu £{tp.toFixed(2)} → your price{" "}
                          <span className="text-base font-bold text-[#C79500]">£{computeRetail(tp, importMargin)}</span> (+{parseFloat(importMargin) || 35}%, rounded up to .99)
                        </span>
                      ) : (
                        "Enter your cost price to see your retail price"
                      );
                    })()}
                  </p>
                  <button data-testid="import-temu-use" onClick={useImported} className="bg-[#E52E39] px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:bg-[#c9202a]">
                    Use these details
                  </button>
                </div>
              </div>
            )}
            {showForm && (
              <ProductForm
                initial={editing || EMPTY_FORM}
                onCancel={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
                onSaved={() => {
                  setShowForm(false);
                  setEditing(null);
                  loadProducts();
                }}
              />
            )}
            {products.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-4 border border-stone-200 bg-[#FFFFFF] p-4">
                <div className="h-20 w-16 shrink-0 overflow-hidden bg-[#FAF8F4]">
                  {(p.images || [])[0] && <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-[200px] flex-1">
                  <p className="font-display text-lg font-bold uppercase leading-tight text-stone-950">
                    {p.name} {p.featured && <span className="ml-2 bg-[#F5C518]/20 px-2 py-0.5 font-mono text-[9px] tracking-widest text-[#C79500]">FEATURED</span>}
                    {!p.active && <span className="ml-2 bg-white/10 px-2 py-0.5 font-mono text-[9px] tracking-widest text-stone-500">HIDDEN</span>}
                  </p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-stone-500">
                    {p.category} • {gbp(p.price)}{p.temu_price ? ` • cost ${gbp(p.temu_price)}` : ""} • {p.delivery_time} • sizes {(p.sizes || []).join("/")}
                  </p>
                  {p.temu_link && (
                    <a href={p.temu_link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block font-mono text-[10px] uppercase tracking-widest text-[#C79500] hover:text-stone-950">
                      Temu link ↗
                    </a>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(p)}
                    className="border border-stone-300 px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-stone-700 hover:border-[#F5C518] hover:text-[#C79500]"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteProduct(p)}
                    className="flex h-9 w-9 items-center justify-center border border-stone-300 text-stone-500 hover:border-[#E52E39] hover:text-[#E52E39]"
                    aria-label={`Delete ${p.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Package className="h-8 w-8 animate-pulse text-[#C79500]" />
      </div>
    );
  if (!user) return <LoginView />;
  return <Dashboard />;
}
